/**
 * Component scoring config — COMPS definition, CompScore type, summary
 * generator, and buildCompScores() factory. Extracted from AnalysisPage
 * so the admin Extraction Inspector can render the same 5-tile row
 * against a freshly-fetched AnalysisResult.
 *
 * The 5 v2 components:
 *   pricing · fee_load · structure · conditions · security
 *
 * Each has a fixed color independent of score zone — matches the
 * landing-page hero scheme.
 */
import { Percent, DollarSign, Clock, FileText, Shield } from 'react-feather';
import type { Analysis, ComponentDetails, ExtractedData } from '../../../api/types';

export const COMP_COLORS = [
  { css: 'rgb(var(--orange))',  textCls: 'text-orange',  bgCls: 'bg-orange-light'  },
  { css: 'rgb(var(--warning))', textCls: 'text-warning', bgCls: 'bg-warning-light' },
  { css: 'rgb(var(--teal))',    textCls: 'text-teal',    bgCls: 'bg-teal-light'    },
  { css: 'rgb(var(--info))',    textCls: 'text-info',    bgCls: 'bg-info-light'    },
  { css: 'rgb(var(--success))', textCls: 'text-success', bgCls: 'bg-success-light' },
] as const;

export const COMPS = [
  { key: 'pricing' as const,    label: 'Interest Rate', weight: 35, scoreField: 'margin_score',     Icon: Percent,    weightAliases: ['pricing', 'interest_rate'],    compColor: COMP_COLORS[0] },
  { key: 'fee_load' as const,   label: 'Fees',          weight: 10, scoreField: 'fee_score',        Icon: DollarSign, weightAliases: ['fee_load', 'fees'],             compColor: COMP_COLORS[1] },
  { key: 'structure' as const,  label: 'Structure',     weight: 20, scoreField: 'structure_score',  Icon: Clock,      weightAliases: ['structure'],                    compColor: COMP_COLORS[2] },
  { key: 'conditions' as const, label: 'Conditions',    weight: 20, scoreField: 'covenant_score',   Icon: FileText,   weightAliases: ['conditions'],                   compColor: COMP_COLORS[3] },
  { key: 'security' as const,   label: 'Collateral',    weight: 15, scoreField: 'collateral_score', Icon: Shield,     weightAliases: ['security', 'collateral'],       compColor: COMP_COLORS[4] },
] as const;

export type CKey = (typeof COMPS)[number]['key'];
export type ScoreBasis = 'extracted' | 'inferred_absent' | 'default';

export type CompScore = Omit<(typeof COMPS)[number], 'weight'> & {
  weight: number;
  score: number | null;
  details: ComponentDetails | null;
  /**
   * HANDOFF_DASHBOARD_EXTRACTION_FIDELITY Step 9 — renders an info
   * label below the score when === 'inferred_absent'.
   */
  scoreBasis: ScoreBasis | null;
};

/**
 * Short one-line interpretation shown in CompCard below the score.
 * Prefers `component_details.interpretation` when present; otherwise
 * derives a phrase from component_details.percentile + ext fields.
 */
export function getSummary(
  key: string,
  score: number,
  d: ComponentDetails | null,
  ext?: ExtractedData,
): string {
  if (d?.details?.interpretation) {return d.details.interpretation as string;}
  switch (key) {
    case 'pricing':
    case 'interest_rate':
      if (d?.percentile) {return `Margin at P${Math.round(d.percentile)} in cohort`;}
      if (ext?.margin_bps) {return `${ext.margin_bps} bps margin`;}
      return score >= 75 ? 'Strong rate terms' : score >= 55 ? 'Mid-range rate terms' : 'Elevated rate terms';
    case 'fee_load':
    case 'fees':
      if (d?.percentile) {return `Total fees ${d.percentile < 50 ? 'below' : 'above'} P${Math.round(d.percentile)}`;}
      return score >= 75 ? 'Light fee structure' : score >= 55 ? 'Standard fee load' : 'Heavy fee structure';
    case 'structure':
      return ext?.maturity_years
        ? `${ext.maturity_years}-yr tenor ${score >= 70 ? 'above' : 'near'} median`
        : score >= 75 ? 'Flexible structure' : score >= 55 ? 'Standard structure' : 'Tight structure';
    case 'conditions':
      return score < 65 ? 'Tight covenant structure' : score >= 80 ? 'Favorable covenants' : 'Standard covenants';
    case 'security':
    case 'collateral':
      return score < 50 ? 'Personal guarantee required' : score >= 70 ? 'Light security package' : 'Moderate security';
    default:
      return '';
  }
}

/**
 * Build the 5-tile CompScore array from an AnalysisResult + dynamic
 * weights_used. Pure function — pull out of any React render loop.
 */
export function buildCompScores(analysis: Analysis | null | undefined): CompScore[] {
  if (!analysis) {return [];}
  const cd = analysis.component_details || {};
  const wu = analysis.weights_used || {};
  return COMPS.map((c) => {
    let score: number | null = null;
    let details: ComponentDetails | null = null;
    const d = cd[c.key as keyof typeof cd];
    if (d && d.score != null) {
      score = d.score;
      details = d;
    } else if (analysis[c.scoreField as keyof Analysis] != null) {
      score = analysis[c.scoreField as keyof Analysis] as number;
      details = d ?? null;
    } else {
      details = d ?? null;
    }
    let weight = c.weight as number;
    for (const alias of c.weightAliases) {
      if (wu[alias] != null) {
        const w = wu[alias] as number;
        weight = w <= 1 ? Math.round(w * 100) : Math.round(w);
        break;
      }
    }
    if (d?.weight_effective != null && !Object.keys(wu).length) {
      const w = d.weight_effective as number;
      weight = w <= 1 ? Math.round(w * 100) : Math.round(w);
    }
    const basisRaw = analysis.score_basis?.[c.key];
    const scoreBasis: ScoreBasis | null =
      basisRaw === 'extracted' || basisRaw === 'inferred_absent' || basisRaw === 'default'
        ? basisRaw
        : null;
    return { ...c, weight, score, details, scoreBasis };
  });
}
